
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts';
import { useHeaderData } from '@/hooks/useHeaderData';
import { SearchBar, LanguageSwitcher, ThemeToggle } from './ui';
import { AdaptiveUsageWidget, UsageWidgetErrorBoundary } from '@/components/UsageWidget';

const Header: React.FC = () => {
  const { dropdownRef, isLoggedIn, isDropdownOpen, getUserInitials, logout, setIsDropdownOpen } = useHeaderData();
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useLanguage();
  
  // Get usage widget variant from localStorage
  const [widgetVariant, setWidgetVariant] = useState<'badge' | 'footer' | 'floating' | 'icon'>(() => {
    const stored = localStorage.getItem('usage_widget_variant');
    return (stored as 'badge' | 'footer' | 'floating' | 'icon') || 'badge';
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      // Simple threshold - only add shadow, don't change size
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for changes to widget variant preference
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('usage_widget_variant');
      const newVariant = (stored as 'badge' | 'footer' | 'floating' | 'icon') || 'badge';
      setWidgetVariant(newVariant);
    };

    // Listen for custom event (same window) and storage event (other windows/tabs)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('widgetVariantChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('widgetVariantChanged', handleStorageChange);
    };
  }, []);

  return (
    <header className={`w-full bg-brand-light-gray dark:bg-slate-950 border-b border-gray-100 dark:border-slate-800 sticky top-0 z-50 py-2 sm:py-3 transition-shadow duration-300 ease-in-out ${isScrolled ? 'shadow-md' : ''}`}>
      <div className="container mx-auto px-2 sm:px-3 md:px-4 lg:px-8">
        {/* Una sola fila: Logo | SearchBar | Nav */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
          {/* Logo + Preview Tag - Izquierda */}
          <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 flex-shrink-0">
            <Link to="/" className="no-underline hover:opacity-80 transition-opacity" data-testid="logo-link">
              {/* Logo Image - will be replaced when asset is provided */}
              <div className="flex items-center">
                {/* TODO: Replace with actual logo image */}
                <img
              src="/assets/logos/promptshelf-logo.svg"
              alt="PromptShelf.AI"
              className="w-auto h-10 sm:h-12 md:h-16 lg:h-20 transition-opacity duration-300 ease-in-out"
                  onError={(e) => {
                    // Fallback to text if image not found
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) {
                      nextElement.style.display = 'block';
                    }
                  }}
                />
                <div className="text-base sm:text-lg md:text-xl font-bold text-brand-dark-blue hidden">
                  PromptShelf.AI
                </div>
              </div>
            </Link>
            {/* Preview Access Tag */}
            <span className="bg-green-500 text-white px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold shadow-sm whitespace-nowrap">
              Preview Access
            </span>
          </div>
          
          {/* SearchBar - Centro, flexible */}
          {isLoggedIn && (
            <div className="flex-1 min-w-0 max-w-full">
              <SearchBar />
            </div>
          )}
          
          {/* Nav - Derecha */}
          <nav className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 lg:space-x-4 xl:space-x-8 flex-shrink-0">
            <LanguageSwitcher />
            <ThemeToggle />
            
            {/* Links solo en desktop */}
            <Link
              to="/teams"
              className="text-brand-dark-blue dark:text-slate-300 hover:text-brand-electric-blue dark:hover:text-slate-100 text-xs sm:text-sm font-medium hidden md:block transition-colors"
            >
              {t('preOrderTeamsAccess')}
            </Link>
            
            {isLoggedIn && (
              <>
                <Link
                  to="/usage"
                  className="text-brand-dark-blue dark:text-slate-300 hover:text-brand-electric-blue dark:hover:text-slate-100 text-xs sm:text-sm font-medium hidden md:block transition-colors"
                >
                  {t('dashboard')}
                </Link>
                {/* Usage Icon Widget - Only show when variant is 'icon' */}
                {widgetVariant === 'icon' && (
                  <UsageWidgetErrorBoundary>
                    <AdaptiveUsageWidget variant="icon" />
                  </UsageWidgetErrorBoundary>
                )}
                
                {/* User Dropdown - Hasta la derecha */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-7 h-7 sm:w-8 sm:h-8 bg-brand-electric-blue text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-medium hover:bg-brand-dark-blue transition-colors shadow-sm"
                    aria-label="User menu"
                  >
                    {getUserInitials()}
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 py-2 z-50">
                      {/* Links para móvil - Pre-Order Teams Access y Dashboard */}
                      <div className="md:hidden">
                        <Link
                          to="/teams"
                          className="flex items-center px-4 py-3 text-sm text-brand-dark-blue dark:text-slate-100 hover:bg-brand-light-gray dark:hover:bg-slate-700 transition-colors"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          {t('preOrderTeamsAccess')}
                        </Link>
                        {isLoggedIn && (
                          <Link
                            to="/usage"
                            className="flex items-center px-4 py-3 text-sm text-brand-dark-blue dark:text-slate-100 hover:bg-brand-light-gray dark:hover:bg-slate-700 transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            {t('dashboard')}
                          </Link>
                        )}
                        <div className="border-t border-gray-100 dark:border-slate-700 my-1"></div>
                      </div>
                      
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-3 text-sm text-brand-dark-blue dark:text-slate-100 hover:bg-brand-light-gray dark:hover:bg-slate-700 transition-colors"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        {t('settings')}
                      </Link>
                      <Link
                        to="/billing?view=subscription"
                        className="flex items-center px-4 py-3 text-sm text-brand-electric-orange dark:text-orange-400 hover:bg-brand-sand-beige dark:hover:bg-slate-700 transition-colors font-medium"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        🚀 {t('upgrade')}
                      </Link>
                      <Link
                        to="/billing"
                        className="flex items-center px-4 py-3 text-sm text-brand-dark-blue dark:text-slate-100 hover:bg-brand-light-gray dark:hover:bg-slate-700 transition-colors"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        {t('billing')}
                      </Link>
                      <div className="border-t border-gray-100 dark:border-slate-700 my-1"></div>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          logout();
                        }}
                        className="flex items-center w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-700 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                      >
                        {t('logout')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            {!isLoggedIn && (
              <>
                <Link
                  to="/login"
                  className="text-brand-dark-blue dark:text-slate-300 hover:text-brand-electric-blue dark:hover:text-slate-100 text-xs sm:text-sm font-medium transition-colors"
                >
                  {t('login')}
                </Link>
                <Link
                  to="/signup"
                  className="bg-brand-electric-blue dark:bg-brand-electric-blue text-white dark:text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-brand-dark-blue dark:hover:bg-blue-600 transition-colors shadow-sm"
                >
                  {t('signUp')}
                </Link>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export { Header };

// Export memoized version for better performance
export default React.memo(Header);
